import { useState, useEffect } from "react";
import { useParams, useSearchParams, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { CheckCircle, Calendar, MapPin, Car, User, Phone, Mail, ArrowLeft, Loader2, Navigation, Clock, CreditCard, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Texas Star
const TexasStar = ({ className = "", size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className}>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
  </svg>
);

const BookingConfirmation = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const sessionId = searchParams.get('session_id');
  const paymentParam = searchParams.get('payment');
  const paymentIntentParam = searchParams.get('payment_intent');
  const isSuccessPage = location.pathname.startsWith('/success');

  useEffect(() => {
    fetchBooking();
  }, [bookingId]);

  useEffect(() => {
    // Check payment status on success page or with payment intent
    if (isSuccessPage || paymentIntentParam) {
      checkPaymentStatusByBooking();
    } else if (sessionId && paymentParam === 'success') {
      checkPaymentStatus(sessionId);
    }
  }, [sessionId, paymentParam, paymentIntentParam, isSuccessPage]);

  const fetchBooking = async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
      // If on success page, immediately mark as paid if backend confirms
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('paid');
      }
    } catch (err) {
      setError("Booking not found");
    } finally {
      setLoading(false);
    }
  };

  const checkPaymentStatusByBooking = async (attempts = 0) => {
    if (attempts >= 5) {
      setPaymentStatus('pending');
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('paid');
        setBooking(response.data);
      } else if (attempts < 4) {
        setTimeout(() => checkPaymentStatusByBooking(attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Payment status check error:', error);
    } finally {
      setCheckingPayment(false);
    }
  };

  const checkPaymentStatus = async (sid, attempts = 0) => {
    if (attempts >= 5) {
      setPaymentStatus('pending');
      return;
    }

    setCheckingPayment(true);
    try {
      const response = await axios.get(`${API}/payments/status/${sid}`);
      if (response.data.payment_status === 'paid') {
        setPaymentStatus('paid');
        fetchBooking(); // Refresh booking data
      } else {
        setTimeout(() => checkPaymentStatus(sid, attempts + 1), 2000);
      }
    } catch (error) {
      console.error('Payment status check error:', error);
      setPaymentStatus('error');
    } finally {
      setCheckingPayment(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-white/50 text-lg mb-4">{error}</p>
          <Link to="/" className="btn-primary">Return Home</Link>
        </div>
      </div>
    );
  }

  const isPaid = booking?.payment_status === 'paid' || paymentStatus === 'paid';

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="booking-confirmation-page">
      {/* Header */}
      <header className="glass">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-4">
          <Link to="/" className="text-white/50 hover:text-white transition-colors">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#D4AF37] flex items-center justify-center">
              <TexasStar size={20} className="text-black" />
            </div>
            <div>
              <span className="font-serif text-xl text-white">Booking Confirmation</span>
              <p className="text-white/40 text-[10px]">by Ivarge LLC</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        {/* Status Message */}
        <div className="text-center mb-12 animate-fade-in-up">
          {checkingPayment ? (
            <>
              <div className="w-20 h-20 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-[#D4AF37] animate-spin" />
              </div>
              <h1 className="font-serif text-4xl text-white mb-3">Processing Payment...</h1>
              <p className="text-white/50">Please wait while we confirm your payment</p>
            </>
          ) : isPaid ? (
            <>
              <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-500" />
              </div>
              <h1 className="font-serif text-4xl text-white mb-3">Payment Confirmed!</h1>
              <p className="text-white/50">Your ride is booked and paid. See you soon!</p>
            </>
          ) : (
            <>
              <div className="w-20 h-20 bg-[#D4AF37]/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-10 h-10 text-[#D4AF37]" />
              </div>
              <h1 className="font-serif text-4xl text-white mb-3">Booking Created</h1>
              <p className="text-white/50">Payment is pending. Please complete payment to confirm.</p>
            </>
          )}
          <p className="text-white/30 text-sm mt-4">
            Confirmation ID: <span className="text-[#D4AF37]">{booking.id.slice(0, 8).toUpperCase()}</span>
          </p>
        </div>

        {/* Payment Status Badge */}
        {isPaid && (
          <div className="flex items-center justify-center gap-2 mb-8 p-3 bg-green-500/10 rounded-lg border border-green-500/20">
            <CreditCard className="w-5 h-5 text-green-500" />
            <span className="text-green-500 font-medium">Payment Complete - ${booking.total_price}</span>
          </div>
        )}

        {/* Booking Details Card */}
        <div className="glass rounded-lg p-8 space-y-6 animate-fade-in-up delay-200" data-testid="booking-details-card">
          {/* Vehicle */}
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_luxury-ride-book-2/artifacts/tbx2qzv1_IMG_0018.jpeg"
              alt="GMC Yukon XL"
              className="w-24 h-16 object-cover rounded"
            />
            <div>
              <p className="text-white font-serif text-lg">GMC Yukon XL</p>
              <p className="text-white/50 text-sm">
                {booking.passengers} passenger{booking.passengers > 1 ? "s" : ""}, {booking.luggage} bag{booking.luggage !== 1 ? "s" : ""}
              </p>
            </div>
          </div>

          <div className="h-px bg-white/10" />

          {/* Route */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Pickup</p>
                <p className="text-white text-sm">{booking.pickup_address}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Navigation className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Dropoff</p>
                <p className="text-white text-sm">{booking.dropoff_address}</p>
              </div>
            </div>
          </div>

          <div className="h-px bg-white/10" />

          {/* Date & Time */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <Calendar className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Date & Time</p>
                <p className="text-white">{booking.pickup_date}</p>
                <p className="text-white/60 text-sm">{booking.pickup_time}</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Trip Details</p>
                <p className="text-white">{booking.distance_miles} miles</p>
                <p className="text-white/60 text-sm">{booking.duration_minutes} min estimated</p>
              </div>
            </div>
          </div>

          <div className="h-px bg-white/10" />

          {/* Contact */}
          <div className="flex items-start gap-3">
            <User className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-white/40 text-xs uppercase tracking-widest mb-1">Contact</p>
              <p className="text-white font-medium">{booking.customer_name}</p>
              <div className="flex items-center gap-4 mt-1 text-white/60 text-sm">
                <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{booking.customer_email}</span>
                <span className="flex items-center gap-1"><Phone className="w-4 h-4" />{booking.customer_phone}</span>
              </div>
            </div>
          </div>

          <div className="h-px bg-white/10" />

          {/* Total */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/40 text-xs uppercase tracking-widest">Total Amount</p>
              <p className="text-white/50 text-xs mt-1">{isPaid ? "Paid" : "Payment pending"}</p>
            </div>
            <p className="price-display text-5xl font-medium" data-testid="confirmation-total-price">
              ${booking.total_price}
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10">
          <Link to="/" className="btn-secondary w-full sm:w-auto text-center" data-testid="book-another-btn">
            Book Another Ride
          </Link>
        </div>

        {/* Contact Info */}
        <div className="mt-12 text-center text-white/40 text-sm">
          <p>Questions? Contact us:</p>
          <p className="mt-2">
            <a href="tel:6823228644" className="text-[#D4AF37] hover:text-white transition-colors">(682) 322-8644</a>
            {" • "}
            <a href="mailto:reservationsluxury@ivarge.com" className="text-[#D4AF37] hover:text-white transition-colors">reservationsluxury@ivarge.com</a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default BookingConfirmation;
